import styled from 'styled-components'
import SauceClipIcon from 'assets/img/sauceclip_icon.png'
import { getLocation } from 'core/util/default'
import { useLocation } from 'react-router-dom'
import { GAHelper } from 'core/ga'
import { GA_EVENT } from 'core/ga/enums'
import { getConfig } from 'core/singleton'
import { useAutoPlay } from 'core/hooks/useAutoPlay'
import { moveToLink } from 'core/util/url'
import { useRecoilValue } from 'recoil'
import { STORE_AIRBRIDGE } from 'core/store/analysis'
import { LINK_MOVE_TYPE } from 'core/constants'
import { IS_OPEN_LINK } from 'core/store/global'
import { clipResourceUrl } from 'core/api'
import { useState } from 'react'

interface Props {
  data: ClipCurationData
}

export default function SauceClipCard({
  data: { channel, clipSource, description, shortUrl, thumbnailUrl },
}: Props) {
  const location = useLocation()
  const { sauceclip } = getConfig()
  const { mediaRef } = useAutoPlay(sauceclip?.isAutoPlay ?? false)
  const airbridge = useRecoilValue(STORE_AIRBRIDGE)
  const isOpenLink = useRecoilValue(IS_OPEN_LINK)
  const [hasError, setHasError] = useState(false)

  const handleError = () => {
    setHasError(true)
  }

  const handleClickCard = () => {
    new GAHelper().sendEvent(GA_EVENT.CLICK_CLIP_CONTENT, {
      partnersId: String(sauceclip?.partnerId),
      broadcastName: description,
      location: getLocation(location),
      position: 'clip',
      broadcastTypeCode: 'clip',
    })
    moveToLink({
      airbridge,
      link: shortUrl,
      type: isOpenLink ? LINK_MOVE_TYPE.REPLACE : LINK_MOVE_TYPE.REDIRECT,
    })
  }

  if (hasError || !clipSource?.key) return null

  return (
    <CardLayout>
      <CardWrapper ref={mediaRef} onClick={handleClickCard}>
        <CardVideoWrapper>
          <CardIcon src={SauceClipIcon} />
          <CardVideo
            onError={handleError}
            className="card_video"
            data-src={clipSource?.key ? `${clipResourceUrl}${clipSource?.key}` : ``}
            muted
            playsInline
            title={description}
          />
          <CardThumbnail
            src={thumbnailUrl ? `${clipResourceUrl}${thumbnailUrl}` : ''}
            className="card_image"
          />
        </CardVideoWrapper>
        <CardVideoTitle>{description}</CardVideoTitle>
        {sauceclip?.isStore && (
          <CardVideoCompanyWrapper>
            <CardVideoCompanyImage
              src={channel?.profileUrl ? `${clipResourceUrl}${channel?.profileUrl}` : ''}
            />
            <CardVideoCompanyText>{channel?.name}</CardVideoCompanyText>
          </CardVideoCompanyWrapper>
        )}
      </CardWrapper>
    </CardLayout>
  )
}

const CardLayout = styled.div`
  * {
    margin: 0;
    padding: 0;
  }
`

const CardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  cursor: pointer;
  width: 274px;
  min-width: 274px;
  max-width: 274px;

  @media (max-width: 768px) {
    width: 211px;
    min-width: 211px;
    max-width: 211px;
  }
`

const CardIcon = styled.img`
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 10;
  width: 32px;
  height: auto;
`

const CardVideoWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 366px;
  background-color: #000;
  border-radius: 7px;
  margin-bottom: 12px;
  overflow: hidden;
  isolation: isolate;

  @media (max-width: 768px) {
    height: 281px;
  }
`

const CardThumbnail = styled.div<{ src: string }>`
  width: 100%;
  height: 100%;
  background-repeat: no-repeat;
  background-image: url('${({ src }) => src}');
  background-size: cover;
  background-position: center;
  position: absolute;
  top: 0;
  opacity: 1;
  transition: all ease 2s;
`

const CardVideo = styled.video.attrs((props) => ({
  onError: props.onError,
}))`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 140%;
  margin-bottom: 0;
  margin-top: 0;
  opacity: 0;
  transition: all ease 2s;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);

  &.video.loaded {
    opacity: 1;
  }

  &.video.loaded + .card_image {
    opacity: 0;
  }
`

const CardVideoTitle = styled.p`
  font-size: 14px;
  line-height: 20px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #000;
  font-weight: 600;
`

const CardVideoCompanyWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  margin-top: 8px;
`

const CardVideoCompanyImage = styled.div<{ src: string }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: solid 1px #edeff0;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  margin-right: 8px;
  background-image: url(${({ src }) => `${src}?h=56&w=56&f=jpg`});
`

const CardVideoCompanyText = styled.p`
  font-size: 12px;
  line-height: 16px;
  color: #999;
  font-weight: 700;

  @media (max-width: 768px) {
    font-weight: 500;
  }
`
